/**
 * Migration 2 — Lead Tracker. Zod schemas for the per-unit upsert,
 * per-building bulk action, work-events CRUD, and the rollup query
 * filters. The DB-level CHECK constraints duplicate the
 * `test_result ↔ positive_count` invariant so a malformed direct
 * write still fails; this layer surfaces the same rule as a friendly
 * 400 before the round-trip.
 */

import { z } from 'zod';

import {
  EXEMPTION_STATUSES,
  FRICTION_STATUSES,
  TEST_RESULTS,
  type ExemptionStatus,
  type TestResult,
} from './lead-constants.js';

// ---------- Per-unit upsert ----------

/** Helper: optional ISO date string `YYYY-MM-DD`, normalised to `null`
 *  when the caller wants to clear the value. Accepts `undefined` to
 *  mean "leave alone". */
const optionalDate = z
  .string()
  .trim()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
  .nullable()
  .optional();

const optionalUuid = z.string().uuid().nullable().optional();

const optionalNotes = z
  .string()
  .max(8_000, 'Notes are capped at 8 000 characters')
  .nullable()
  .optional();

/** Body for `PUT /api/v1/units/:id/lead`. Every field is optional —
 *  the server upserts a row and only touches the fields the client
 *  actually sent. A field set to `null` clears it; a field omitted is
 *  left at its current value. */
export const upsertUnitLeadStatusInput = z
  .object({
    friction_status: z.enum(FRICTION_STATUSES).optional(),
    exemption_status: z.enum(EXEMPTION_STATUSES).optional(),
    exemption_letter_document_id: optionalUuid,
    test_result: z.enum(TEST_RESULTS).optional(),
    positive_count: z
      .number()
      .int()
      .min(1, 'Positive count must be at least 1')
      .max(10_000)
      .nullable()
      .optional(),
    latest_xrf_date: optionalDate,
    latest_xrf_report_document_id: optionalUuid,
    notes: optionalNotes,
    /** Optional free-form note attached to the resulting history row,
     *  e.g. "retested after remediation". Never written to
     *  `unit_lead_status.notes` — that's a separate column. */
    change_note: z.string().trim().max(1_000).nullable().optional(),
  })
  .superRefine((v, ctx) => {
    // The DB-level CHECK is the authoritative invariant; we duplicate
    // it here to short-circuit obvious mistakes (positive without a
    // count, count without `positive`) with a clean error message.
    if (v.test_result === 'positive') {
      if (v.positive_count == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['positive_count'],
          message: 'positive_count is required when test_result is "positive"',
        });
      }
    } else if (v.test_result !== undefined && v.positive_count != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['positive_count'],
        message: 'positive_count must be null unless test_result is "positive"',
      });
    }
  });
export type UpsertUnitLeadStatusInput = z.infer<typeof upsertUnitLeadStatusInput>;

/**
 * Server-side helper that re-applies the invariant check after the
 * patch has been merged with the existing row. The schema above only
 * sees the *delta*; this runs against the *resulting* state and
 * mirrors the DB CHECK. Returns `null` if the combination is valid,
 * or a human-readable message describing the violation otherwise.
 */
export function validateLeadStatusInvariant(
  testResult: TestResult,
  positiveCount: number | null,
): string | null {
  if (testResult === 'positive') {
    if (positiveCount == null || positiveCount < 1) {
      return 'positive_count is required (≥ 1) when test_result is "positive"';
    }
    return null;
  }
  if (positiveCount != null) {
    return 'positive_count must be null unless test_result is "positive"';
  }
  return null;
}

/**
 * Mirror of the same invariant from the exemption side: an exemption
 * status of `approved` *may* (but isn't required to) carry an
 * attachment id; an `exemption_letter_document_id` set while
 * `exemption_status` is anything other than `approved` is a bug.
 * Returns `null` if valid, or a message otherwise.
 */
export function validateExemptionAttachment(
  exemptionStatus: ExemptionStatus,
  letterDocumentId: string | null,
): string | null {
  if (letterDocumentId && exemptionStatus !== 'approved') {
    return 'exemption_letter_document_id can only be set when exemption_status is "approved"';
  }
  return null;
}

// ---------- Work events ----------

export const createLeadWorkEventInput = z.object({
  performed_on: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD'),
  description: z
    .string()
    .trim()
    .min(1, 'Description is required')
    .max(2_000, 'Description is capped at 2 000 characters'),
  contractor: z
    .string()
    .trim()
    .max(200)
    .nullable()
    .optional()
    .transform((v) => (v === undefined ? null : v)),
  document_ids: z.array(z.string().uuid()).max(50).optional().default([]),
});
export type CreateLeadWorkEventInput = z.infer<typeof createLeadWorkEventInput>;

export const updateLeadWorkEventInput = z
  .object({
    performed_on: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
      .optional(),
    description: z.string().trim().min(1).max(2_000).optional(),
    contractor: z.string().trim().max(200).nullable().optional(),
    document_ids: z.array(z.string().uuid()).max(50).optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'Provide at least one field to update',
  });
export type UpdateLeadWorkEventInput = z.infer<typeof updateLeadWorkEventInput>;

// ---------- Per-building bulk action ----------

export const bulkMarkFrictionCleanInput = z.object({
  unit_ids: z
    .array(z.string().uuid())
    .min(1, 'Provide at least one unit')
    .max(500, 'Bulk action is capped at 500 units per call'),
  change_note: z.string().trim().max(1_000).nullable().optional(),
});
export type BulkMarkFrictionCleanInput = z.infer<typeof bulkMarkFrictionCleanInput>;

// ---------- Per-building grid query ----------

const filterCsv = (allowed: readonly string[]) =>
  z
    .string()
    .trim()
    .min(1)
    .transform((v) => v.split(','))
    .pipe(z.array(z.enum(allowed as readonly [string, ...string[]])).min(1));

export const buildingLeadGridQuery = z.object({
  friction_status: filterCsv(FRICTION_STATUSES).optional(),
  exemption_status: filterCsv(EXEMPTION_STATUSES).optional(),
  test_result: filterCsv(TEST_RESULTS).optional(),
  /** Convenience filter: only units with at least one positive XRF. */
  has_positives: z
    .union([z.literal('true'), z.literal('false')])
    .optional()
    .transform((v) => v === 'true' ? true : v === 'false' ? false : undefined),
  /** Convenience filter: only units that are still `untested` overall. */
  untested: z
    .union([z.literal('true'), z.literal('false')])
    .optional()
    .transform((v) => v === 'true' ? true : v === 'false' ? false : undefined),
  /** Convenience filter: only units where the friction surfaces are
   *  marked clean (regardless of whether they were tested too). */
  friction_clean: z
    .union([z.literal('true'), z.literal('false')])
    .optional()
    .transform((v) => v === 'true' ? true : v === 'false' ? false : undefined),
  q: z.string().trim().max(120).optional(),
});
export type BuildingLeadGridQuery = z.infer<typeof buildingLeadGridQuery>;

// ---------- Org-wide list query ----------

export const leadUnitsListQuery = buildingLeadGridQuery.extend({
  building_id: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(200).optional().default(50),
  cursor: z.string().trim().min(1).max(120).optional(),
});
export type LeadUnitsListQuery = z.infer<typeof leadUnitsListQuery>;

// ---------- Rollup query (`GET /lead/rollup`) ----------

export const leadRollupQuery = z.object({
  /** Optional comma-separated list of building UUIDs to scope every
   *  rollup section. When omitted, the rollup spans the whole org. */
  building_ids: z
    .string()
    .trim()
    .min(1)
    .transform((v) => v.split(','))
    .pipe(z.array(z.string().uuid()).min(1).max(200))
    .optional(),
});
export type LeadRollupQuery = z.infer<typeof leadRollupQuery>;

// ---------- History query ----------

export const leadHistoryQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).optional().default(50),
});
export type LeadHistoryQuery = z.infer<typeof leadHistoryQuery>;
